import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getEvent, updateEvent, deleteEvent } from "../../services/events" // updateEvent generic?
import { getAPIBaseURL } from "../../services/helpers"
import { updateData, deleteData } from "../../services/request"
import {
    CalendarDaysIcon,
    MapPinIcon,
    ArrowLeftIcon,
    TrashIcon,
    EyeSlashIcon,
    EyeIcon,
    TicketIcon
} from "@heroicons/react/24/outline"

const AdminEventDetails = () => {
    const { eventId } = useParams()
    const navigate = useNavigate()
    const [event, setEvent] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [submitting, setSubmitting] = useState(false)

    const fetchEvent = async () => {
        setLoading(true)
        setError("")
        try {
            const res = await getEvent(eventId)
            if (res.status === 200) {
                // getEvent returns { event, ticketTypes }, so we need to extract both
                if (res.data && res.data.event) {
                    // Add ticketTypes to the event object
                    const eventData = { ...res.data.event, ticketTypes: res.data.ticketTypes || [] }
                    setEvent(eventData)
                } else if (res.data && res.data._id) {
                    // Sometimes it might return the event directly, but ticketTypes might be separate
                    const eventData = res.data.ticketTypes ? { ...res.data, ticketTypes: res.data.ticketTypes } : res.data
                    setEvent(eventData)
                } else {
                    setError("Invalid event data format")
                }
            } else {
                setError(res.error || "Event not found")
            }
        } catch (err) {
            setError("Failed to load event")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchEvent() }, [eventId])

    const handleActivate = async () => {
        if (!window.confirm('Are you sure you want to activate this event? This will make it visible to attendees.')) return

        setSubmitting(true)
        setError("")
        setSuccess("")
        const params = new URLSearchParams()
        params.append('status', 'PUBLISHED')
        
        try {
            const res = await updateEvent(eventId, params.toString())
            if (res.status === 200) {
                setSuccess("Event activated successfully")
                fetchEvent()
            } else {
                setError(res.error || "Failed to activate event")
            }
        } catch (err) {
            setError("Failed to activate event")
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeactivate = async () => {
        if (!window.confirm('Are you sure you want to deactivate this event? This will hide it from attendees.')) return

        setSubmitting(true)
        setError("")
        setSuccess("")
        const params = new URLSearchParams()
        params.append('status', 'DRAFT')
        
        try {
            const res = await updateEvent(eventId, params.toString())
            if (res.status === 200) {
                setSuccess("Event deactivated successfully")
                fetchEvent()
            } else {
                setError(res.error || "Failed to deactivate event")
            }
        } catch (err) {
            setError("Failed to deactivate event")
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm("Are you sure? This will delete the event and potentially affect orders.")) return

        setSubmitting(true)
        const res = await deleteEvent(eventId)
        if (res.status === 200) {
            navigate('/admin/events')
        } else {
            setError(res.error || "Delete failed")
            setSubmitting(false)
        }
    }

    if (loading) return <div className="text-center py-20">Loading...</div>
    if (!event) return <div className="text-center py-20">Event not found</div>

    return (
        <div className="max-w-4xl mx-auto">
            <Link to="/admin/events" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
                <ArrowLeftIcon className="w-4 h-4" /> Back to Events
            </Link>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Banner */}
                <div className="h-48 bg-slate-100 relative">
                    {event.coverImage ? (
                        <img src={event.coverImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <CalendarDaysIcon className="w-12 h-12" />
                        </div>
                    )}
                    <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase shadow-sm ${event.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : 'bg-white text-slate-700'
                            }`}>
                            {event.status}
                        </span>
                    </div>
                </div>

                <div className="p-8">
                    {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl">{error}</div>}
                    {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl">{success}</div>}

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex-1">
                            <h1 className="text-2xl font-bold text-slate-900">{event.title}</h1>
                            <p className="text-slate-500 mt-1">
                                Organized by <span className="font-medium text-slate-700">{event.centerId?.organization || 'Unknown Center'}</span>
                                {event.centerId?.email && (
                                    <span className="text-slate-400 ml-2">({event.centerId.email})</span>
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Event Details</h3>
                            <div className="flex items-start gap-3 text-slate-600">
                                <CalendarDaysIcon className="w-5 h-5 shrink-0 mt-0.5 text-slate-400" />
                                <div>
                                    <div className="font-medium text-slate-900">Start Date</div>
                                    <div>{event.startsAt ? new Date(event.startsAt).toLocaleString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    }) : 'N/A'}</div>
                                    {event.endsAt && (
                                        <>
                                            <div className="font-medium text-slate-900 mt-2">End Date</div>
                                            <div>{new Date(event.endsAt).toLocaleString('en-US', { 
                                                year: 'numeric', 
                                                month: 'long', 
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-start gap-3 text-slate-600">
                                <MapPinIcon className="w-5 h-5 shrink-0 mt-0.5 text-slate-400" />
                                <div>
                                    <div className="font-medium text-slate-900">Venue</div>
                                    <div>{event.venue || 'N/A'}</div>
                                    {event.location && (event.location.city || event.location.state || event.location.country) && (
                                        <div className="mt-2">
                                            <div className="font-medium text-slate-900">Location</div>
                                            <div>{[event.location.city, event.location.state, event.location.country].filter(Boolean).join(', ') || 'N/A'}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {event.slots && (
                                <div className="flex items-start gap-3 text-slate-600">
                                    <TicketIcon className="w-5 h-5 shrink-0 mt-0.5 text-slate-400" />
                                    <div>
                                        <div className="font-medium text-slate-900">Available Slots</div>
                                        <div>{event.slots}</div>
                                    </div>
                                </div>
                            )}
                            <div className="mt-6">
                                <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">Description</h4>
                                <div className="prose prose-sm text-slate-600 max-w-none whitespace-pre-wrap">
                                    {event.description || 'No description provided'}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Ticket Types</h3>
                            {event.ticketTypes && event.ticketTypes.length > 0 ? (
                                <div className="space-y-3">
                                    {event.ticketTypes.map((tt, i) => (
                                        <div key={tt._id || i} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <span className="font-semibold text-slate-900">{tt.name}</span>
                                                    {tt.description && (
                                                        <p className="text-xs text-slate-500 mt-1">{tt.description}</p>
                                                    )}
                                                </div>
                                                <span className="text-lg font-bold text-cyan-600">₦{tt.price?.toLocaleString()}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 text-xs text-slate-600 mt-3 pt-3 border-t border-slate-200">
                                                <div>
                                                    <span className="text-slate-500">Quantity:</span>
                                                    <span className="font-medium ml-1">{tt.quantity || 0}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500">Remaining:</span>
                                                    <span className="font-medium ml-1">{tt.remaining ?? tt.quantity ?? 0}</span>
                                                </div>
                                                <div>
                                                    <span className="text-slate-500">Status:</span>
                                                    <span className={`ml-1 font-medium ${tt.status === 'ACTIVE' ? 'text-green-600' : 'text-slate-400'}`}>
                                                        {tt.status || 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500 text-sm">No ticket types defined for this event.</p>
                            )}
                        </div>
                    </div>

                    <div className="pt-8 border-t border-slate-100 flex flex-wrap gap-4">
                        {event.status === 'PUBLISHED' ? (
                            <button
                                onClick={handleDeactivate}
                                disabled={submitting}
                                className="flex items-center gap-2 px-4 py-2 border border-orange-200 bg-orange-50 text-orange-700 rounded-lg font-medium hover:bg-orange-100 transition"
                            >
                                <EyeSlashIcon className="w-4 h-4" />
                                Deactivate Event
                            </button>
                        ) : (
                            <button
                                onClick={handleActivate}
                                disabled={submitting}
                                className="flex items-center gap-2 px-4 py-2 border border-green-200 bg-green-50 text-green-700 rounded-lg font-medium hover:bg-green-100 transition"
                            >
                                <EyeIcon className="w-4 h-4" />
                                Activate Event
                            </button>
                        )}
                        <button
                            onClick={handleDelete}
                            disabled={submitting}
                            className="ml-auto flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition"
                        >
                            <TrashIcon className="w-4 h-4" /> Delete Event
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminEventDetails
